import { effect, inject } from '@angular/core';
import { signalStoreFeature, withHooks } from '@ngrx/signals';
import { AlertService } from './alert.service';

/**
 * Alert Feature for SignalStore
 *
 * Generic feature that automatically detects and displays error alerts from the "error" property.
 * Works with any signalStore to provide automatic error handling.
 *
 * Requirements:
 * - Store must have an "error" property (signal)
 * - The consuming store is responsible for managing/setting the error value
 *
 * Supports:
 * - String errors: directly displayed
 * - Error objects with userMessage property: error.userMessage displayed (added by interceptors)
 * - Error objects with message property: error.message displayed
 * - Null/undefined: ignored
 *
 * Features:
 * - Automatic error detection from "error" property
 * - Deduplication: same error won't be shown multiple times
 * - Reactive: responds to error signal changes automatically
 * - Follows standard error extraction pattern: userMessage || message || fallback
 *
 * Usage:
 * ```typescript
 * export const MyStore = signalStore(
 *   withComputed((store) => ({
 *     error: computed(() => store.apiError() ?? store.validationError() ?? null)
 *   })),
 *   withAlert(), // Will automatically show error alerts
 *   ...
 * );
 * ```
 */
export function withAlert() {
  return signalStoreFeature(
    withHooks((store) => ({
      onInit() {
        const alertService = inject(AlertService);

        // Track last shown error to prevent duplicates
        let lastShownError: string | null = null;

        // Create effect that monitors the error property
        effect(() => {
          // Check if store has error property and it's a function (signal)
          if ('error' in store && typeof store.error === 'function') {
            try {
              const errorSignal = store.error as () =>
                | string
                | null
                | undefined
                | { userMessage?: string; message?: string };
              const errorValue = errorSignal();

              if (errorValue) {
                // Extract error message following the standard pattern: userMessage || message || fallback
                const errorMessage =
                  typeof errorValue === 'string'
                    ? errorValue
                    : (typeof errorValue === 'object' &&
                        (errorValue.userMessage || errorValue.message)) ||
                      'Error desconocido';

                // Only show if message exists and is different from last shown
                if (errorMessage.trim() && lastShownError !== errorMessage) {
                  alertService.showError(errorMessage);
                  lastShownError = errorMessage;
                }
              } else {
                // Clear tracking when error is resolved
                lastShownError = null;
              }
            } catch (error) {
              console.warn('withAlert: Could not read error property:', error);
            }
          }
        });
      },
    })),
  );
}
